import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, getBit, timeConverterSM} from '../../../Utils';
import axios from 'axios';
import Spinner from '../../common/generic/Spinner';
import { resourceDomain } from '../../../ApiHelper';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;
    const [skillsJSON, setSkillsJSON] = useState({});

    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getProgramMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getProgramMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getProgramMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, skills:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }
        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getProgramMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.id.toLowerCase(),
                    func:'getProgramMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    const fetchSkillsJSON = async () => {
        if(selectedProgram.id==='') return;
        const response = await axios.get(`${resourceDomain}programs/${selectedProgram.id.toLowerCase()}/data/progstruct/skills.json`);
        setSkillsJSON({...response});
    };      

    // convert database['getProgramMarks'] into setMarkRecords
    // UDATES - when database['getProgramMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getProgramMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getProgramMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getProgramMarks'].isLoaded,
            data: database['getProgramMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});
        fetchSkillsJSON();
    }, [database['getProgramMarks']]);  

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;
        if(isObjectEmpty(skillsJSON)) return;

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                // Since the module json files load many times, make sure the marks don't over-right themselves
                if(!markRecords[studentID]) return acc; // still loading marks
                let studentMarkRecords = JSON.parse(JSON.stringify(markRecords[studentID].data));
               
                let marksPerModule = modulesData.map(moduleRec => {
                    
                    let moduleID = moduleRec.moduleID;
                    return moduleRec.links.map(linkRec =>
                    {
                        let linkID= linkRec.linkID;
                        if(studentMarkRecords[`${studentID}+${moduleID[0]}+${linkID}`]) {
                            return {moduleID, linkID, name: linkRec.name, unitName: linkRec.unit.name, mark: studentMarkRecords[`${studentID}+${moduleID[0]}+${linkID}`]};
                        }
                    }).filter(e=> e) // remove empties
                    .reduce((acc, markRec)=>{ // find all skill marks for the module
                        let skills = skillsJSON.data[markRec.moduleID[0]];
                        let markSkill = skills.reduce((skillAcc,skillRec)=> { // find the skill with the matching linkID for the mark
                            if(skillRec.activities.indexOf(markRec.linkID)>-1)
                                skillAcc = skillRec;
                            return skillAcc;
                        },{});

                        markRec.mark.QuestionsAttempted = parseInt(markRec.mark.QuestionsAttempted);
                        markRec.mark.QuestionsRight = parseInt(markRec.mark.QuestionsRight);

                        if(markSkill.desc in acc){ // merge duplicate skill marks together
                            let lastMark = acc[markSkill.desc];
                            markRec.mark.QuestionsAttempted += lastMark.mark.QuestionsAttempted;
                            markRec.mark.QuestionsRight += lastMark.mark.QuestionsRight;
                        }

                        acc[markSkill.desc] = {...markRec};

                        let skillsGroup = skillsJSON.data[markRec.moduleID[0]+'_GROUP'];
                        if(!skillsGroup) return acc; // no skills per question
                        
                        let markSkillGroup = skillsGroup.reduce((skillAcc,skillRec)=> { // find the skill with the matching linkID for the mark
                            if(skillRec.activities.indexOf(markRec.linkID)>-1)
                                skillAcc = skillRec;
                            return skillAcc;
                        },{});

                        // add any skills that are by question using the GROUP section of the skills.json
                        if(!isObjectEmpty(markSkillGroup)){
                            let skillKeys = markSkillGroup.skills.split(',');
                            skillKeys.forEach((skillKey, i)=>{
                                let mark = getBit(parseInt(markRec.mark.QuestionScoreMask),i);
                                let parentSkill = skills.find(e=>e.name===skillKey);

                                if(!parentSkill){
                                    console.log('Missing Skill Group', skillKey, markRec.linkID, markRec.moduleID[0]);
                                    return;
                                }

                                if(parentSkill.desc in acc){ 
                                    acc[parentSkill.desc].mark.QuestionsAttempted +=1;
                                    acc[parentSkill.desc].mark.QuestionsRight +=mark;
                                }
                                else{
                                    acc[parentSkill.desc] ={
                                        mark:{
                                            QuestionsAttempted:1,
                                            QuestionsRight:mark
                                        }
                                    };
                                }
                            });
                        }

                        return acc;
                        }
                    ,{});
                });

                // add the module title(s) to the marks
                marksPerModule = marksPerModule.map((markRec, i) =>{
                    return{
                        data: markRec,
                        moduleID: modulesData[i].moduleID
                    };
                });

                let combinedMarks = {};
                let hasCombinedMarks = false;
                marksPerModule.forEach((markRec)=>{
                    Object.keys(markRec.data).map((skillKey) => {
                        if(!(skillKey in combinedMarks)){ // merge all marks as combined marks            
                            combinedMarks[skillKey] = {
                                mark:{
                                    QuestionsAttempted:0,
                                    QuestionsRight:0
                                }
                            };
                        }
                        else{
                            hasCombinedMarks = true;
                        }

                        combinedMarks[skillKey].mark.QuestionsAttempted += markRec.data[skillKey].mark.QuestionsAttempted;
                        combinedMarks[skillKey].mark.QuestionsRight += markRec.data[skillKey].mark.QuestionsRight;
                    });
                });
                if(hasCombinedMarks)
                    acc[`${studentRec.id}+combined`] = combinedMarks;      

                acc[`${studentRec.id}+${studentRec.name}`] = marksPerModule;
                return acc;
            },{});        
            
            let combinedMarks = {};
            // find all the missing skills from the json file and display them in the console
            Object.keys(studentsModuleMarks).map((studentKey) => {
                if(studentKey.endsWith('combined')) return;

                studentsModuleMarks[studentKey].map(descriptions=>{
                    Object.keys(descriptions.data).map((key) => {

                        if(!(key in combinedMarks)){ // merge all marks as combined marks
                            combinedMarks[key] = {
                                mark:{
                                    QuestionsAttempted:0,
                                    QuestionsRight:0
                                }
                            };
                        }

                        combinedMarks[key].mark.QuestionsAttempted += descriptions.data[key].mark.QuestionsAttempted;
                        combinedMarks[key].mark.QuestionsRight += descriptions.data[key].mark.QuestionsRight;

                        if(key==='undefined')
                            console.log('Skill Missing',descriptions.data[key]);
                    });
                });
            });
            setProcessedMarks(studentsModuleMarks);
            
            if(csvData){
                let newLine = '%0A';
                let csvString = '';
                csvString =`""${newLine}`;
                csvString +=`"Students without marks will not appear in this report"${newLine}`;
                let tableHeaderStr = '"Skill","Questions Correct","Questions Attempted","Percent"'+newLine;

                Object.keys(studentsModuleMarks).forEach((key) => { 
                    if(key.endsWith('combined')) return;
                    let headerStr = `${newLine}"${key.split('+')[1]}"${newLine}"${selectedProgram.name}"${newLine}"Skill Report"${newLine}`;
                    let tableData  = studentsModuleMarks[key];
                    csvString += headerStr + tableHeaderStr;

                    tableData.forEach((moduleRec) => {
                        let moduleTitleStr = (!isObjectEmpty(moduleRec.data)) ? moduleRec.moduleID[1]+newLine : '';
                        csvString += moduleTitleStr;
                        Object.keys(moduleRec.data).map(key=>{
                            const { mark } = moduleRec.data[key];
                            const { QuestionsRight, QuestionsAttempted } = mark;
                            let desc = key;
                            let recordStr = `"${desc}","${QuestionsRight}","${QuestionsAttempted}",${Math.round((QuestionsRight/QuestionsAttempted)*100)}%${newLine}`;
                            csvString += recordStr;
                        });
                    });

                    let combinedMarks = studentsModuleMarks[`${key.split('+')[0]}+combined`];
                    if(combinedMarks && !isObjectEmpty(combinedMarks)){
                        csvString += '"Combined Scores"'+newLine+'"Combines duplicate skills across all modules"'+newLine;
                        Object.keys(combinedMarks).map((markKey) => {
                            if(key!=='undefined'){
                                const { mark } = combinedMarks[markKey];
                                const { QuestionsRight, QuestionsAttempted } = mark;
                                let desc = markKey;
                                let recordStr = `"${desc}","${QuestionsRight}","${QuestionsAttempted}",${Math.round((QuestionsRight/QuestionsAttempted)*100)}%${newLine}`;
                                csvString += recordStr;
                            } 
                        });
                    }
                });
                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, skills:false});
                    return;
                }
                if(Object.keys(studentsModuleMarks).length==1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'SkillsReport_'+selectedProgram.name+'_'+today,csvString.replace(/[#]/gi, ''));
                //uploadCSV('skillReport',replaceAll(csvString,'#',''));
                csvData.setloadingCSV({...csvData.loadingCSV, skills:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);
    useEffect(() =>{tryLoadingNow();},[skillsJSON]);  

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {Object.keys(processedMarks).map((key, i) => (<span key={i}> 
            <><br/>
            {(!key.endsWith('combined')) && <SkillsReportComp header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key]} combinedMarks={processedMarks[`${key.split('+')[0]}+combined`]}/>}
            <br/></>
        </span>))}
    </>;
};

const SkillsReportComp = ({header, tableData, combinedMarks}) => {
    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
    <Col xs='5'><Row>
        <Col style={{textAlign: 'left'}} xs='12'> Skill </Col>
        </Row>
    </Col>
    <Col xs='7'><Row>
        <Col style={{textAlign: 'center'}} xs='3'>Questions Correct</Col>
        <Col style={{textAlign: 'center'}} xs='3'>Questions Attempted</Col>
        <Col style={{textAlign: 'center'}} xs='3'>Percent</Col>
        </Row></Col>
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'>{header.student}</Col> 
            <Col xs='4'>{header.program} </Col>
            <Col xs='4'>Skills Report</Col>
        </Row></h4>
    </center>;

    const Footer = () => <hr/>;
    return (
    <>
        {headerComp}
        <hr/>
        {titleRow}
            {tableData.map((moduleRec, module_i) => <span key={module_i}>
                {(!isObjectEmpty(moduleRec.data)) && 
                    <Row><Col xs='12' style={{fontWeight:'bold'}} >{moduleRec.moduleID[1]}</Col></Row>}
                    {Object.keys(moduleRec.data).map((key, i_mark) => (                 
                        (key!=='undefined') && <ProgramRow key={i_mark} desc={key} data={moduleRec.data[key]} fontWeight={'normal'}/>
                    ))}                
            </span>
        )}
        {(combinedMarks && !isObjectEmpty(combinedMarks)) && <><br/>
            <center><h4>Combined Scores</h4>( Combines duplicate skills across all modules )</center><br/>
            {titleRow}
            {Object.keys(combinedMarks).map((key, i_mark) => (                 
                (key!=='undefined') && <ProgramRow key={i_mark} desc={key} data={combinedMarks[key]} fontWeight={'normal'}/>
            ))}
        </>}
        <Footer/>
    </>
    );
};
SkillsReportComp.propTypes = {
    tableData: PropTypes.array
};

const ProgramRow = (props) => {
    const { mark} = props.data;
    const { QuestionsRight, QuestionsAttempted } = mark;

    return (
        <Row style={{fontSize:'0.9em'}}>
            <Col xs='5'><Row>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='12'> {props.desc} </Col>
                </Row>
            </Col>
            <Col xs='7'><Row>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{QuestionsRight}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{QuestionsAttempted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='3'>{Math.round((QuestionsRight/QuestionsAttempted)*100)}%</Col>
            </Row></Col>
        </Row>
    );
};

ProgramRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;